import React from "react";
import { MyReferralCode } from "../../../DataModels/DataModels";

interface ActiveShareCardProps {
	referral: MyReferralCode;
	onCopyLink?: (code: string) => void;
	onRevoke?: (code: string) => void;
}

const SinglePersonIcon: React.FC = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		className="text-primary-active"
	>
		<circle cx="12" cy="8" r="4" />
		<path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
	</svg>
);

const GroupPersonIcon: React.FC = () => (
	<svg
		width="28"
		height="20"
		viewBox="0 0 32 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		className="text-primary-active"
	>
		<circle cx="10" cy="8" r="3" />
		<path d="M3 20c0-3.5 3-6 7-6s7 2.5 7 6" />
		<circle cx="22" cy="8" r="3" />
		<path d="M18 20c0-3.5 2-5 4-5s4 1.5 4 5" />
	</svg>
);

const ActiveShareCard: React.FC<ActiveShareCardProps> = ({
	referral,
	onCopyLink,
	onRevoke
}) => {
	const isFriend = referral.referralType === "friend";
	const isOverflow = !isFriend && referral.currentUses > referral.maxUses;
	const overflowPercent = isFriend
		? 0
		: Math.round((referral.currentUses / referral.maxUses) * 100);
	const progressPercent = isFriend ? 0 : Math.min(100, overflowPercent);

	const formatDate = (dateStr: string) => {
		return new Date(dateStr).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric"
		});
	};

	const canRevoke =
		referral.status === "pending" || referral.status === "active";

	return (
		<div className="bg-primary-accent border border-primary-inactive rounded-lg p-3">
			<div className="flex items-center gap-3">
				<div className="flex-shrink-0">
					{isFriend ? <SinglePersonIcon /> : <GroupPersonIcon />}
				</div>
				<div className="flex-grow min-w-0">
					<p className="text-primary-active text-sm font-medium truncate">
						{isFriend
							? `Invite for ${referral.recipientContact}`
							: referral.label}
					</p>
					<p className="text-primary-scrim text-xs">
						{isFriend
							? `${referral.status === "redeemed" ? "Redeemed" : "Pending"} · Created ${formatDate(referral.createdAt)}`
							: `${referral.currentUses} of ${referral.maxUses} used · ${referral.status === "active" ? "Active" : referral.status}`}
					</p>
				</div>
				<div className="flex-shrink-0">
					{isFriend ? (
						<div
							className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
								referral.status === "redeemed"
									? "border-primary-active"
									: "border-primary-inactive"
							}`}
						>
							<svg
								width="12"
								height="12"
								viewBox="0 0 12 12"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								className={
									referral.status === "redeemed"
										? "text-primary-active"
										: "text-primary-inactive"
								}
							>
								<path d="M2 6l3 3 5-5" />
							</svg>
						</div>
					) : isOverflow ? (
						<span className="text-sm font-bold text-success-active">
							{overflowPercent}%
						</span>
					) : (
						<div className="w-12 h-2 bg-primary-background rounded-full overflow-hidden">
							<div
								className="h-full rounded-full bg-success-active"
								style={{ width: `${progressPercent}%` }}
							/>
						</div>
					)}
				</div>
			</div>
			{(onCopyLink || onRevoke) && (
				<div className="flex gap-3 mt-2 ml-8">
					{onCopyLink && (
						<button
							className="text-xs text-primary-active underline cursor-pointer"
							onClick={() => onCopyLink(referral.code)}
						>
							Copy Link
						</button>
					)}
					{onRevoke && canRevoke && (
						<button
							className="text-xs text-error-active underline cursor-pointer"
							onClick={() => onRevoke(referral.code)}
						>
							Revoke
						</button>
					)}
				</div>
			)}
		</div>
	);
};

ActiveShareCard.displayName = "ActiveShareCard";

export default ActiveShareCard;
export type { ActiveShareCardProps };
